<template>
    <div class="previewing-screen">
        <div class="screen-content">
            <div class="controls-upper px-4">
                <div class="hstack justify-content-center position-relative px-4 flex-grow-1">
                    <button class="btn btn-none p-0 position-absolute start-0" @click="backStep">
                        <Icon symbol="chevron-left"></Icon>
                    </button>
                    <p class="heading-s mb-0 text-center">Kad Raya Anda Siap Untuk Dikongsi!</p>
                </div>
            </div>
            <div v-if="videoBlob" class="video-content">
                <video ref="previewVideoRef" id="preview-video" autoplay controls loop playsinline></video>
            </div>
            <div v-if="imageString" class="img-content">
                <img ref="previewImageRef" id="preview-img" class="img-fluid" alt="tnb-card">
            </div>
            <div class="controls-bottom">
                <FadeInOut mode="out-in" :duration="200">
                    <div v-if="isTranscoding" class="vstack justify-content-center gap-4 px-4 flex-grow-1">
                        <p class="heading-s text-center mb-0">Sila tunggu sekitar 60 saat sementara kad anda dihasilkan.</p>
                        <div class="vstack align-items-center">
                            <div>
                                <Spinner width="64" height="64"></Spinner>
                            </div>
                        </div>
                    </div>
                    <div v-else class="vstack justify-content-center gap-3 p-3 px-4 flex-grow-1">
                        <div class="hstack gap-3 justify-content-center">
                            <button v-if="isSupported" class="btn btn-tnb-gradient preview-action-btn"
                                @click="shareToSocialMedia">Kongsi <Share2 :size="16" class="ms-1" />
                            </button>
                            <a :href="downloadHref" class="btn btn-preview-download preview-action-btn" :download="fileName">
                                <span>Muat Turun</span> <Download :size="16" class="ms-1 preview-download-icon" />
                            </a>
                        </div>

                        <div class="preview-promo-banner">
                            <div class="preview-promo-content">
                                <p class="preview-promo-title">Setiap Hero Bersedia Menghadapi Masa Depan.</p>
                                <p class="preview-promo-desc">Ambil langkah hari ini untuk melindungi apa yang paling bermakna. Terokai bagaimana perlindungan takaful dapat membantu anda melindungi insan tersayang.</p>
                                <a href="https://www.prubsn.com.my/ms/rezeki-hero-raya-lead-form/?utm_source=Raya+Card+Portal&utm_medium=Thank+You+Page&utm_campaign=Raya+Ecard&utm_id=Raya+Ecard" target="_blank" class="preview-promo-link">Ketahui Lebih Lanjut</a>
                            </div>
                        </div>

                        <a class="btn btn-none p-0 text-center preview-cipta-lagi" @click.prevent="backToHomePage" href="#">
                            Cipta Lagi <RotateCw :size="14" class="ms-1 preview-cipta-lagi-icon" />
                        </a>
                        
                    </div>
                </FadeInOut>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { usePreviewStore } from '../../stores';
import { useShare } from '@vueuse/core'
import { FadeInOut } from "vue3-transitions";
import { urltoFile } from '@/modules/core/utilities';
import { Icon, Spinner } from '@/modules/core/components';
import { storeToRefs } from 'pinia';
import { Share2, Download, RotateCw } from 'lucide-vue-next';

const emits = defineEmits(['back', 'restart']);

const fileName = ref();
const { share, isSupported } = useShare();
const previewVideoRef = ref<HTMLVideoElement>();
const previewImageRef = ref<HTMLImageElement>();
const downloadHref = ref('');

const previewStore = usePreviewStore();
const { videoBlob, imageString, selectedMediaType } = storeToRefs(previewStore);
const sharingFile = ref<File | null>();
const isTranscoding = ref(true);

const setBlobIntoVideo = () => {
    if (!previewVideoRef.value) return;
    if (!videoBlob.value) return;

    let blob = new Blob([videoBlob.value], { type: 'video/mp4' });
    const createObjectURL = window.URL.createObjectURL || window.webkitURL.createObjectURL;

    previewVideoRef.value.src = createObjectURL(blob);
    downloadHref.value = previewVideoRef.value.src;

    previewVideoRef.value.onloadedmetadata = () => {
        if (!previewVideoRef.value) return;
        previewVideoRef.value.play();
    };

    previewVideoRef.value.addEventListener('play', onVideoPlay);

    sharingFile.value = new File([videoBlob.value], fileName.value, { type: 'video/mp4' })
}

const setImageIntoImg = () => {
    if (!previewImageRef.value) return;
    if (!imageString.value) return;

    previewImageRef.value.src = imageString.value;
    downloadHref.value = previewImageRef.value.src;

    urltoFile(imageString.value, fileName.value, 'image/png')
        .then(function (file) {
            sharingFile.value = file
        });

}

const onVideoPlay = () => {
  console.log('onVideoPlay(): video generated and play succesfully.');
}

const backStep = () => {
    emits('back');
}

const backToHomePage = () => {
    videoBlob.value = null;
    imageString.value = null;
    selectedMediaType.value = null;
    emits('restart');
}

const shareToSocialMedia = async () => {
    if (!sharingFile.value) return;

    let sharingItem = {
        files: [sharingFile.value],
    }

    share(sharingItem);
}

onMounted(async () => {
    if (videoBlob.value) {
        fileName.value = 'PruBSN-rezeki-hero-raya.mp4';
        setBlobIntoVideo();
        
    } else {
        fileName.value = 'PruBSN-rezeki-hero-raya.png';
        setImageIntoImg();
    }

    isTranscoding.value = false;
})

</script>

<style lang="scss" scoped>
.previewing-screen {
    position: relative;
    width: 100%;    height: 100%;
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background-color: #ffffff;

    .screen-content {
        z-index: 1;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow-y: auto;
    }

    .controls-upper {
        display: flex;
        background-image: url('/images/prubsn/layout/frame.png');
        background-position: top center;
        background-repeat: no-repeat;
        background-size: 100% auto;

        .hstack {
            margin-top: 20px;
            margin-bottom: 20px;
        }
    }

    .controls-bottom {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
    }

    @include media-breakpoint-up(md) {
        height: 750px;
    }

}

video {
    width: 100%;
    height: auto;
}

.preview-action-btn {
    flex: 1;
    min-width: 0;
}

.preview-promo-banner {
    border-radius: 0.75rem;
    overflow: hidden;
    background-image: url('/images/prubsn/layout/frame.png'), url('/images/prubsn/layout/frame.png');
    background-position: top center, bottom center;
    background-repeat: no-repeat, no-repeat;
    background-size: 100% auto, 100% auto;
    background-color: #1ab5b0;
}

.preview-promo-content {
    padding: 1.25rem 1.5rem;
    text-align: center;

    .preview-promo-title {
        font-weight: 700;
        font-size: 1rem;
        color: #ffffff;
        margin-bottom: 0.5rem;
    }

    .preview-promo-desc {
        font-size: 0.8rem;
        color: #ffffff;
        margin-bottom: 0.75rem;
        line-height: 1.4;
    }

    .preview-promo-link {
        font-size: 0.85rem;
        font-weight: 600;
        color: #DDB56D;
        text-decoration: underline;
        text-underline-offset: 3px;
    }
}

.btn-tnb-gradient {
    background-image: url('/images/svg/red-button-small.svg');
    font-size: 12px;
    padding: 0.6rem 1rem;
    min-height: 50px;
}

.btn-preview-download {
    background-image: url('/images/svg/white-button-small.svg');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center center;
    background-color: transparent;
    border: none;
    border-radius: 0;
    white-space: nowrap;
    transition: filter .3s ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    overflow: visible;
    font-size: 12px;
    padding: 0.6rem 1rem;
    min-height: 50px;

    @media (max-height: 799px) {
        font-size: 12px;
        padding: 0.6rem 1rem;
    }

    @media (max-height: 700px) {
        font-size: 11px;
        padding: 0.4rem 0.75rem;
    }

    @media (min-height: 1000px) {
        font-size: 11px;
        padding: 0.4rem 0.75rem;
    }

    span {
        background: linear-gradient(90deg, #A47020, #DDB56D);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }

    .preview-download-icon {
        color: #DDB56D;
    }

    &:hover,
    &:focus {
        filter: brightness(0.95);
    }
}

.preview-cipta-lagi {
    background: linear-gradient(180deg, #FF0303, #FF5F5F);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-decoration: underline;
    text-decoration-color: #FF0303;
    text-underline-offset: 4px;
    font-weight: 450;
    font-size: 16px;

    .preview-cipta-lagi-icon {
        color: #FF0303;
        -webkit-text-fill-color: initial;
    }
}
</style>